import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usd } from '../api/client';
import { usePriceCurve, useSimulate } from '../api/hooks';
import { Button, Card, Field, Input, Issues, Loading, PageHeader, Panel } from '../components/ui';
import { appliedRateName } from '../i18n/format';

const DEFAULT_START = () => {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/**
 * The simulator is what makes self-serve pricing safe: an owner can ask "a car
 * arriving at 07:40 and staying 9 hours pays what?" and see exactly which rate
 * won and what it beat (FR-A-23 ~ A-25).
 */
export function SimulatorPage({ lotId }: { lotId: string }) {
  const { t } = useTranslation();
  const [startAt, setStartAt] = useState(DEFAULT_START);
  const [duration, setDuration] = useState(540);
  const [curveDuration, setCurveDuration] = useState(240);

  const simulate = useSimulate(lotId);
  const curve = usePriceCurve(lotId, curveDuration);

  const run = () => {
    simulate.mutate({ startAt: new Date(startAt).toISOString(), durationMinutes: duration });
  };

  const result = simulate.data;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t('simulator.title')} hint={t('simulator.hint')} />

      <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
            <Field label={t('simulator.startAt')}>
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </Field>
            <Field label={t('simulator.minutes')}>
              <Input
                className="num"
                type="number"
                min={1}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              />
            </Field>
          </div>
          <Button variant="dark" onClick={run} disabled={simulate.isPending}>
            {t(simulate.isPending ? 'simulator.running' : 'simulator.run')}
          </Button>

          {result ? (
            <Card className="flex flex-col gap-2 bg-surface-muted">
              <Row
                label={t('simulator.requestedMinutes')}
                value={t('simulator.minutesShort', { count: result.breakdown.requestedMinutes })}
              />
              <Row
                label={t('simulator.entitlementMinutes')}
                value={t('simulator.minutesShort', { count: result.breakdown.entitlementMinutes })}
                strong
              />
              <Row
                label={t('simulator.appliedRate')}
                value={appliedRateName(result.breakdown.appliedRate, t)}
                strong
              />
              {result.breakdown.comparedTierBaseCents !== null ? (
                <p className="text-[11px] text-muted">
                  {t('simulator.beatsTier', { price: usd(result.breakdown.comparedTierBaseCents) })}
                </p>
              ) : null}
              <div className="mt-1 flex items-baseline justify-between border-t border-hairline pt-2">
                <span className="text-sm font-bold">{t('simulator.finalPrice')}</span>
                <span className="num text-lg font-bold">{usd(result.breakdown.totalCents)}</span>
              </div>
            </Card>
          ) : null}

          {result ? (
            <Card>
              <h3 className="mb-2 text-[13px] font-bold">{t('simulator.selfCheck')}</h3>
              <Issues issues={result.issues} />
            </Card>
          ) : null}
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between">
            <h3 className="text-[13px] font-bold">{t('simulator.curve')}</h3>
            <label className="flex items-center gap-2 text-[11px] text-muted">
              {t('simulator.curveDuration')}
              <select
                className="rounded border border-hairline px-2 py-1 text-xs"
                value={curveDuration}
                onChange={(e) => setCurveDuration(Number(e.target.value))}
              >
                {[60, 120, 240, 480, 720, 1440].map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {minutes >= 60
                      ? t('simulator.hours', { count: minutes / 60 })
                      : t('simulator.minutesShort', { count: minutes })}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {curve.isPending ? (
            <Loading />
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(curve.data?.samples ?? []).map((sample) => ({
                    hour: String(sample.hour).padStart(2, '0'),
                    total: sample.totalCents / 100,
                    rate: appliedRateName(sample.appliedRate, t),
                  }))}
                  margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
                >
                  <CartesianGrid strokeDasharray="2 4" stroke="#e4e3de" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#8a8a84' }} interval={2} />
                  <YAxis tick={{ fontSize: 10, fill: '#8a8a84' }} />
                  <Tooltip
                    formatter={(value, _name, item) => [
                      `$${Number(value ?? 0).toFixed(2)}`,
                      (item?.payload as { rate?: string } | undefined)?.rate ?? t('simulator.total'),
                    ]}
                    labelFormatter={(label) => t('simulator.arrivesAt', { hour: label })}
                  />
                  <Bar dataKey="total" fill="#0b0b0b" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <Panel>{t('simulator.panel')}</Panel>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between text-[12.5px]">
      <span className="text-muted">{label}</span>
      <span className={strong ? 'num font-semibold' : 'num'}>{value}</span>
    </div>
  );
}
